import { If, location, onMount, reactive, watch } from 'cdui-js';
import type { AgreementSceneDocItem } from '../../../api/agreement-doc';
import i18n from '../../../i18n';
import { openAgreementDoc } from '../../../lib/agreement-doc-view';
import { agreementLinkLabel } from '../../../lib/agreementLinkLabel';
import { bootDepositApp, isFundHideNav } from '../deposit/h5-deposit/appBridge';
import { showToast } from '../components/Toast';
import { IconBack, IconCheckOff, IconCheckOn } from './icons';
import {
  activateTradeProduct,
  ensureSysConfigurationsReady,
  isChinaAMarketFullyClosed,
  isChinaConnectGranted,
  isDerivativesGranted,
  leaveTradeProducts,
  loadTradeProductDocs,
  readPreselectFromUrl,
  refreshUserTradePermission,
  resolveInitialSelected,
  successUrl,
  type TradeProductKey,
} from './shared';
import './trade-products.css';

const toastError = (e: unknown) => {
  if (typeof e === 'string') {
    showToast(e);
    return;
  }
  const msg = (e as { message?: string } | null)?.message;
  showToast(msg || i18n.TradeProducts.ActivateFailed);
};

const RadioDot = (props: { on: boolean; disabled: boolean }) => (
  <div class={`tp-radio${props.on ? ' is-on' : ''}${props.disabled ? ' is-disabled' : ''}`.trim()}>
    <If when={!!(props.on || props.disabled)}>
      <div class="tp-radio-dot" />
    </If>
  </div>
);

export const TradeProductsPage = () => {
  const hideNav = isFundHideNav();
  const state = reactive({
    loading: true,
    submitting: false,
    selected: null as TradeProductKey | null,
    agreed: false,
    derivativesGranted: false,
    chinaConnectGranted: false,
    /**
     * A 股沪深都关闭时隐藏 A股通 选项；字典就绪前为 false，避免选项先出现再消失。
     * 后续若改为置灰显示「暂未开放」：改为渲染 disabled 行，并保持 canSubmit 拦截。
     */
    chinaConnectAvailable: false,
    chinaConnectDoc: null as AgreementSceneDocItem | null,
    derivativesRiskDoc: null as AgreementSceneDocItem | null,
  });

  const syncGranted = () => {
    state.derivativesGranted = isDerivativesGranted();
    state.chinaConnectGranted = isChinaConnectGranted();
    state.selected = resolveInitialSelected(
      state.derivativesGranted,
      state.chinaConnectGranted,
      readPreselectFromUrl(),
      state.chinaConnectAvailable
    );
  };

  onMount(() => {
    void bootDepositApp()
      .catch(() => {})
      .then(async () => {
        try {
          await Promise.all([refreshUserTradePermission().catch(() => null), ensureSysConfigurationsReady()]);
          state.chinaConnectAvailable = !isChinaAMarketFullyClosed();
          syncGranted();
          const docs = await loadTradeProductDocs();
          state.chinaConnectDoc = docs.chinaConnectDoc;
          state.derivativesRiskDoc = docs.derivativesRiskDoc;
        } finally {
          state.loading = false;
        }
      });
  });

  watch(
    () => state.selected,
    () => {
      state.agreed = false;
    }
  );

  /** 可见项都已开通（A股通 隐藏时只看衍生品） */
  const bothActivated = () =>
    state.derivativesGranted && (!state.chinaConnectAvailable || state.chinaConnectGranted);
  const showDerivativesContent = () => state.selected === 'derivatives' && !state.derivativesGranted;
  const showChinaConnectAgree = () =>
    state.selected === 'chinaConnect' && state.chinaConnectAvailable && !state.chinaConnectGranted;

  const canSubmit = () => {
    if (bothActivated() || state.submitting || !state.selected) return false;
    if (state.selected === 'derivatives') return !state.derivativesGranted && state.agreed;
    if (state.selected === 'chinaConnect') {
      return state.chinaConnectAvailable && !state.chinaConnectGranted && state.agreed;
    }
    return false;
  };

  const select = (key: TradeProductKey) => {
    if (key === 'derivatives' && state.derivativesGranted) return;
    if (key === 'chinaConnect' && (!state.chinaConnectAvailable || state.chinaConnectGranted)) return;
    state.selected = key;
  };

  const openDerivativesRisk = () => {
    // TODO(trade-product-protocol): 正式衍生品风险披露
    const doc = state.derivativesRiskDoc;
    if (!doc) {
      showToast(i18n.TradeProducts.AgreeDocUnavailable);
      return;
    }
    openAgreementDoc(doc);
  };

  const openChinaConnectAgreement = () => {
    // TODO(trade-product-protocol): 正式 A股通协议声明
    const doc = state.chinaConnectDoc;
    if (!doc) {
      showToast(i18n.TradeProducts.AgreeDocUnavailable);
      return;
    }
    openAgreementDoc(doc);
  };

  const onActivate = async () => {
    if (!state.selected || state.submitting) return;
    if (state.selected === 'derivatives' && !state.agreed) {
      showToast(i18n.TradeProducts.RiskRequired);
      return;
    }
    if (state.selected === 'chinaConnect' && !state.agreed) {
      showToast(i18n.TradeProducts.AgreeRequired);
      return;
    }
    state.submitting = true;
    try {
      await activateTradeProduct(state.selected);
      await refreshUserTradePermission().catch(() => null);
      location.routeTo(successUrl(state.selected), [0, 0]);
    } catch (e) {
      toastError(e);
    } finally {
      state.submitting = false;
    }
  };

  return (
    <div class="trade-products-h5">
      <If when={!hideNav}>
        <div class="nav">
          <button type="button" class="nav-back" onclick={() => leaveTradeProducts()}>
            <IconBack />
          </button>
          <div class="nav-title">{i18n.TradeProducts.Title}</div>
        </div>
      </If>

      <div class="tp-scroll">
        <div
          class={`tp-option${state.derivativesGranted ? ' is-disabled' : ''}`.trim()}
          onclick={() => select('derivatives')}
        >
          <RadioDot on={state.selected === 'derivatives'} disabled={state.derivativesGranted} />
          <span class="tp-option-label">{i18n.TradeProducts.Derivatives}</span>
          <If when={state.derivativesGranted}>
            <span class="tp-activated">{i18n.TradeProducts.Activated}</span>
          </If>
        </div>

        {/* A 股市场关闭时暂为隐藏；后续若改为置灰显示「暂未开放」，在此改为渲染 disabled 行 */}
        <If when={state.chinaConnectAvailable}>
          <div
            class={`tp-option${state.chinaConnectGranted ? ' is-disabled' : ''}`.trim()}
            onclick={() => select('chinaConnect')}
          >
            <RadioDot on={state.selected === 'chinaConnect'} disabled={state.chinaConnectGranted} />
            <span class="tp-option-label">{i18n.TradeProducts.ChinaConnect}</span>
            <If when={state.chinaConnectGranted}>
              <span class="tp-activated">{i18n.TradeProducts.Activated}</span>
            </If>
          </div>
        </If>

        <If when={showDerivativesContent()}>
          <div class="tp-deriv">
            <div class="tp-deriv-title">{i18n.TradeProducts.DerivativesFormTitle}</div>
            <div class="tp-deriv-card">
              <div class="tp-deriv-body">{i18n.TradeProducts.DerivativesFormBody}</div>
              <div class="tp-deriv-link-row">
                <div
                  class="tp-agree-check"
                  onclick={() => {
                    state.agreed = !state.agreed;
                  }}
                >
                  <If when={state.agreed}>
                    <IconCheckOn />
                  </If>
                  <If when={!state.agreed}>
                    <IconCheckOff />
                  </If>
                </div>
                <span
                  class="agreement-link"
                  onclick={(e) => {
                    e.stopPropagation();
                    openDerivativesRisk();
                  }}
                >
                  {agreementLinkLabel(state.derivativesRiskDoc?.name, i18n.TradeProducts.DerivativesRiskLink)}
                </span>
              </div>
            </div>
            <div class="tp-deriv-footer">{i18n.TradeProducts.DerivativesFormFooter}</div>
          </div>
        </If>
      </div>

      <div class="tp-footer">
        <If when={showChinaConnectAgree()}>
          <div class="tp-agree">
            <div
              class="tp-agree-check"
              onclick={() => {
                state.agreed = !state.agreed;
              }}
            >
              <If when={state.agreed}>
                <IconCheckOn />
              </If>
              <If when={!state.agreed}>
                <IconCheckOff />
              </If>
            </div>
            <div class="tp-agree-text">
              <span>{i18n.TradeProducts.AgreePrefix}</span>
              <span
                class="agreement-link"
                onclick={(e) => {
                  e.stopPropagation();
                  openChinaConnectAgreement();
                }}
              >
                {agreementLinkLabel(state.chinaConnectDoc?.name, i18n.TradeProducts.AgreeDocLink)}
              </span>
            </div>
          </div>
        </If>
        <button
          type="button"
          class="tp-submit"
          disabled={!canSubmit() && !state.submitting}
          onclick={() => {
            void onActivate();
          }}
        >
          {bothActivated() ? i18n.TradeProducts.AllActivated : i18n.TradeProducts.Activate}
        </button>
      </div>
    </div>
  );
};

export { TradeProductsSuccess } from './Success';
export default TradeProductsPage;
